import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { INVESTIGATION_USER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { INVESTIGATION_DRAWER_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { Badge, EntityChip, Icon, Icons, Tooltip } from '@silverfort/sf-components';
import RiskChip from '../../modules/nhi/onPrem/components/RiskChip';
import IdentityDataTable from './DataTables/IdentityDataTable';
import GroupDataTable from './DataTables/GroupDataTable';
import OuDataTable from './DataTables/OuDataTable';

const DISABLED_FLAG = 2;

export const entityAdTypeToTitle = {
    [AD_ENTITY_TYPES.USER]: 'Identity',
    [AD_ENTITY_TYPES.SERVER]: 'Resource',
    [AD_ENTITY_TYPES.HOST]: 'Resource',
    [AD_ENTITY_TYPES.IP]: 'Resource',
    [AD_ENTITY_TYPES.OU]: 'Organizational Unit',
    [AD_ENTITY_TYPES.GROUP]: 'Group'
};

const createExtraDataTooltip = (data, field) => {
    const extraData = data.slice(1);
    return (
        <Tooltip
            title={
                <div className="flex flex-col gap-1">
                    {extraData.map((item) => (
                        <div>{item[field]}</div>
                    ))}
                </div>
            }>
            <EntityChip className={'cursor-pointer'} type="border" text={`+${extraData.length}`} />
        </Tooltip>
    );
};

export const getUrlByType = (adType: number) => {
    for (const entityType of Object.values(INVESTIGATION_DRAWER_ENTITY_TYPES)) {
        if (entityType.adType === adType) return entityType.urlData;
    }
};

const getEntityIcon = (adType) => {
    switch (adType) {
        case AD_ENTITY_TYPES.USER:
            return Icons.User;
        case AD_ENTITY_TYPES.SERVER:
            return Icons.Server;
        case AD_ENTITY_TYPES.HOST:
            return Icons.Server;
        case AD_ENTITY_TYPES.GROUP:
            return Icons.UserGroup;
        case AD_ENTITY_TYPES.OU:
            return Icons.Folder;
        default:
            return Icons.User;
    }
};
export const buildInvestigationData = (entityData, adType, onEntityClick, pricingModulesState) => {
    const details = entityData.details;
    return (
        <>
            <div className="min-h-20 px-6 w-full flex items-center justify-between border-b border-sf-gray-100">
                <div className="flex gap-6 h-12 items-center">
                    <div className="p-1 flex items-center justify-center border rounded-lg border-sf-gray-100 h-10 w-10">
                        <Icon icon={getEntityIcon(adType)} size={32} />
                    </div>
                    <div className="w-full h-12 flex flex-col gap-1">
                        <div className="font-medium text-16 h-5 flex items-center gap-1">
                            <span>{details.identifier || details.display_name || details.name}</span>
                            {details.is_deleted && <span className="text-13 text-sf-gray-500">(deleted)</span>}
                            {!!details.user_account_control && !!(details.user_account_control & DISABLED_FLAG) && (
                                <span className="text-13 text-sf-gray-500">(disabled)</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 h-6">
                            {(adType !== AD_ENTITY_TYPES.USER ||
                                details.type === INVESTIGATION_USER_TYPES.AD ||
                                details.type === INVESTIGATION_USER_TYPES.HYBRID) && (
                                <>
                                    <Badge color="secondary" text="Active Directory" icon={'ActiveDirectory'} />
                                    <Badge color="secondary" text={details.domain_name} />
                                </>
                            )}
                            {details.type === INVESTIGATION_USER_TYPES.HYBRID && (
                                <Badge color="secondary" text="Cloud Sync" icon={'IntegrationEntraID'} />
                            )}

                            {details.type === INVESTIGATION_USER_TYPES.ENTRA && (
                                <Badge color="secondary" text="Entra ID" icon={'IntegrationEntraID'} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex px-6 max-h-20">
                {entityData.RiskLevel !== undefined &&
                    (pricingModulesState?.ispm?.anyEnabled || pricingModulesState?.itdr?.anyEnabled) && (
                        <div className="h-20 flex flex-col gap-2 px-4 justify-center">
                            <div className="text-13 text-sf-gray-500 h-4">Risk level</div>
                            <div>
                                <RiskChip risk={entityData.RiskLevel} className="select-text" />
                            </div>
                        </div>
                    )}
                {!!entityData.RiskReasons?.length &&
                    (pricingModulesState?.ispm?.anyEnabled || pricingModulesState?.itdr?.anyEnabled) && (
                        <div className="h-20 flex flex-col gap-2 px-4 justify-center">
                            <div className="text-13 text-sf-gray-500 h-4">Risk indicators</div>
                            <div className="flex gap-1">
                                <EntityChip
                                    className="!min-w-fit select-text"
                                    type="border"
                                    text={entityData.RiskReasons[0].entityTitle}
                                />
                                {entityData.RiskReasons.length > 1 &&
                                    createExtraDataTooltip(entityData.RiskReasons, 'entityTitle')}
                            </div>
                        </div>
                    )}
                {!!entityData.groups?.length && (
                    <div className="h-20 flex flex-col gap-2 px-4 justify-center">
                        <div className="text-13 text-sf-gray-500 h-4">
                            {adType === AD_ENTITY_TYPES.GROUP ? 'Parent groups' : 'AD membership'}
                        </div>
                        <div className="flex gap-1">
                            <EntityChip
                                className="!min-w-fit select-text"
                                type="border"
                                text={entityData.groups[0].name}
                            />
                            {entityData.groups.length > 1 && createExtraDataTooltip(entityData.groups, 'name')}
                        </div>
                    </div>
                )}

                <div className="h-20 flex flex-col gap-2 px-4 justify-center">
                    <div className="text-13 text-sf-gray-500 h-4">Organizational Unit</div>
                    <div className="flex gap-1">
                        <EntityChip
                            className="!min-w-fit select-text"
                            type="border"
                            text={entityData.details.path?.split('\\')?.[0]}
                        />
                    </div>
                </div>
            </div>
            {adType === AD_ENTITY_TYPES.GROUP && (
                <GroupDataTable onEntityClick={onEntityClick} adType={adType} entityData={entityData} />
            )}
            {adType === AD_ENTITY_TYPES.OU && (
                <OuDataTable onEntityClick={onEntityClick} adType={adType} entityData={entityData} />
            )}
            {(adType === AD_ENTITY_TYPES.USER ||
                adType === AD_ENTITY_TYPES.SERVER ||
                adType === AD_ENTITY_TYPES.HOST) && (
                <IdentityDataTable onEntityClick={onEntityClick} adType={adType} entityData={entityData} />
            )}
        </>
    );
};
